import * as React from 'react'
import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {graphql} from 'react-relay'
import {useNavigate} from 'react-router-dom'

import {useUpdateResults} from '../../../contexts/update'
import {useAppDispatch, useAppSelector} from '../../../hooks/react-redux'
import {useOnDemandQuery} from '../../../hooks/relay/useOnDemandQuery'
import {usePermission} from '../../../hooks/usePermission'
import {getBranchesLocator, getIsBranchPresentInBuildTypeArg} from '../../../rest/branches'
import {BuildTypeProperties, getBuildTypeArg} from '../../../rest/buildTypes'
import {getBuildHref, getBuildTypeHref} from '../../../routes'
import {
  getBuildType,
  getBuildTypeParameters,
  getIsBuildStarting,
  getProject,
} from '../../../selectors'
import {restApi} from '../../../services/rest'
import type {BuildId} from '../../../types'
import {getBuildTypeFilter, Permission, stringifyId} from '../../../types'
import {defaultBranch} from '../../../utils/branchNames'
import {getPropertyFromList} from '../../../utils/getProperty'
import {objectToQuery} from '../../../utils/queryParams'
import {useSequenceLoaderReadyHandler} from '../SequenceLoader/SequenceLoader.hooks'
import Visible from '../Visible/Visible'

import type {RunBuildParametersQuery} from './__generated__/RunBuildParametersQuery.graphql'
import RunBuild from './RunBuild'
import {runCustomBuild, triggerBuild} from './RunBuild.actions'
import type {OwnProps} from './RunBuild.types'

const parametersQuery = graphql`
  query RunBuildParametersQuery($locator: String!) {
    buildParametersOfBuildType(btLocator: $locator) {
      property {
        type {
          rawValue
        }
      }
    }
  }
`

type ContainerProps = OwnProps & {
  isVisible: boolean
}
function RunBuildContainer(props: ContainerProps) {
  const {
    buildTypeId,
    branch,
    isRedeploy,
    promoteId,
    rerunBuildId,
    isVisible,
    redirectToQueuedBuild,
    redirectToQueuedBuildTab,
    onTrigger,
    disabled,
  } = props
  const updateResults = useUpdateResults()

  const projectId = useAppSelector(state => getBuildType(state, buildTypeId)?.projectId)
  const isProjectVirtual = useAppSelector(state => getProject(state, projectId)?.virtual)

  const customLabel = useAppSelector(state => {
    const buildType = getBuildType(state, buildTypeId)
    const buildTypeParameters = getBuildTypeParameters(state, buildTypeId)
    return buildType != null && buildTypeParameters != null
      ? getPropertyFromList(BuildTypeProperties.RUN_BUTTON_CAPTION, buildTypeParameters.parameters)
      : null
  })
  const isDeployment = useAppSelector(
    state => getBuildType(state, buildTypeId)?.type === 'deployment',
  )
  const promoteOnly = useAppSelector(state => {
    const buildTypeParameters = getBuildTypeParameters(state, buildTypeId)
    return (
      isDeployment &&
      buildTypeParameters != null &&
      getPropertyFromList(BuildTypeProperties.PROMOTE_ONLY, buildTypeParameters.parameters) ===
        'true'
    )
  })
  const canViewSettings = usePermission(Permission.VIEW_BUILD_CONFIGURATION_SETTINGS, projectId)
  const deployLabel = isRedeploy === true ? 'Redeploy' : 'Deploy'
  const noPermission = !usePermission(Permission.RUN_BUILD, projectId)
  const triggerDisabled = customLabel === '' || isProjectVirtual
  const loader = useAppSelector(state => getIsBuildStarting(state, buildTypeId, branch))
  const parametersLoaded = useAppSelector(state => {
    const buildTypeParameters = getBuildTypeParameters(state, buildTypeId)
    return buildTypeParameters != null
  })
  const isReady = useAppSelector(
    state => getBuildType(state, buildTypeId) != null && parametersLoaded && projectId != null,
  )
  const dispatch = useAppDispatch()

  const navigate = useNavigate()
  const [fetchSingleBuildTypeData] =
    restApi.endpoints.getBuildTypeNormalized.useLazyQuerySubscription()
  useEffect(() => {
    if (!parametersLoaded && isVisible) {
      fetchSingleBuildTypeData(getBuildTypeArg(buildTypeId))
    }
  }, [buildTypeId, fetchSingleBuildTypeData, isVisible, parametersLoaded])
  const [startedBuildId, setStartedBuildId] = useState<BuildId>()
  const onRedirectAfterRun = useCallback(
    (buildId: BuildId | null | undefined) => {
      if (redirectToQueuedBuild === true) {
        if (buildId != null) {
          const buildHref = getBuildHref(buildId)
          navigate(
            redirectToQueuedBuildTab != null
              ? `${buildHref}?${objectToQuery({
                  buildTab: stringifyId(redirectToQueuedBuildTab),
                })}`
              : buildHref,
          )
        } else {
          navigate(getBuildTypeHref(buildTypeId))
        }
      } else if (buildId != null) {
        setStartedBuildId(buildId)
      }
    },
    [buildTypeId, navigate, redirectToQueuedBuild, redirectToQueuedBuildTab],
  )
  const [fetchIsBranchPresent] = restApi.endpoints.checkIfBranchExistsInBuildType.useLazyQuery()
  const fetchBranch = useCallback(
    () =>
      fetchIsBranchPresent(
        getIsBranchPresentInBuildTypeArg(
          buildTypeId,
          getBranchesLocator({
            node: getBuildTypeFilter(buildTypeId),
            includeSnapshots: true,
            excludeGroups: true,
            branch:
              branch == null || branch.groupFlag === true || branch.wildcard === true
                ? defaultBranch
                : branch,
            inPath: true,
          }),
        ),
        true,
      ).then(({data}) => data === true),
    [buildTypeId, branch, fetchIsBranchPresent],
  )
  const fetchBuildTypeParameters = useOnDemandQuery<RunBuildParametersQuery>(parametersQuery)

  const onClick = useCallback(async () => {
    const [isBranchPresent, hasPromptParameter] = await Promise.all([
      fetchBranch(),
      canViewSettings
        ? fetchBuildTypeParameters({locator: `id:${buildTypeId}`})
            .toPromise()
            .then(
              result =>
                result?.buildParametersOfBuildType?.property?.some(
                  item => item.type?.rawValue?.includes(" display='prompt'"),
                ),
            )
        : Promise.resolve(null),
    ])
    const isBranchExcluded = isBranchPresent === false
    const branchToRun = isBranchExcluded ? null : branch

    if (rerunBuildId != null || hasPromptParameter === true || isBranchExcluded) {
      dispatch(
        runCustomBuild(
          buildTypeId,
          branchToRun,
          itemId => {
            onRedirectAfterRun(itemId)
            updateResults()
          },
          hasPromptParameter === true ? 'properties-tab' : isBranchExcluded ? 'changes-tab' : null,
          {
            promoteId,
            init: true,
            stateKey: rerunBuildId != null ? `reRun_${stringifyId(rerunBuildId)}` : 'prompt',
            initFromBuild: rerunBuildId,
          },
        ),
      )
      return
    }

    onTrigger?.()
    const {queuedBuildId, showDialog} =
      (await dispatch(
        triggerBuild(buildTypeId, branchToRun, {
          promoteId,
        }),
      )) ?? {}
    onRedirectAfterRun(queuedBuildId)

    if (queuedBuildId != null) {
      updateResults()
    }

    if (showDialog) {
      dispatch(
        runCustomBuild(
          buildTypeId,
          branchToRun,
          itemId => {
            onTrigger?.()
            onRedirectAfterRun(itemId)
            updateResults()
          },
          'properties-tab',
          {
            promoteId,
            init: true,
            stateKey: 'prompt',
          },
        ),
      )
    }
  }, [
    branch,
    buildTypeId,
    canViewSettings,
    dispatch,
    fetchBranch,
    fetchBuildTypeParameters,
    onRedirectAfterRun,
    onTrigger,
    promoteId,
    rerunBuildId,
    updateResults,
  ])
  const onSplitButtonClick = useCallback(async () => {
    const isBranchPresent = await fetchBranch()

    dispatch(
      runCustomBuild(
        buildTypeId,
        isBranchPresent ? branch : null,
        itemId => {
          onRedirectAfterRun(itemId)
          updateResults()
        },
        null,
        {
          promoteId,
        },
      ),
    )
  }, [branch, buildTypeId, dispatch, fetchBranch, onRedirectAfterRun, promoteId, updateResults])

  useSequenceLoaderReadyHandler(isReady)

  return (
    <RunBuild
      {...props}
      disabled={
        disabled === true || noPermission || triggerDisabled || (promoteOnly && promoteId == null)
      }
      loader={loader}
      noPermission={noPermission}
      triggerDisabled={triggerDisabled}
      startedBuildId={startedBuildId}
      setStartedBuildId={setStartedBuildId}
      onClick={onClick}
      onSplitButtonClick={rerunBuildId != null ? null : onSplitButtonClick}
    >
      {rerunBuildId != null ? 'Re-run...' : customLabel || (isDeployment ? deployLabel : 'Run')}
    </RunBuild>
  )
}

export default function RunBuildOuterContainer(props: OwnProps) {
  const wasVisible = useRef(false)
  const placeholder = (
    <RunBuild {...props} disabled>
      {'Run'}
    </RunBuild>
  )
  return (
    <Suspense fallback={placeholder}>
      {props.withVisibilityDetect !== false ? (
        <Visible>
          {isVisible => {
            wasVisible.current ||= isVisible
            return wasVisible.current ? (
              <RunBuildContainer {...props} isVisible={isVisible} />
            ) : (
              placeholder
            )
          }}
        </Visible>
      ) : (
        <RunBuildContainer {...props} isVisible />
      )}
    </Suspense>
  )
}
